import { useCallback, useState } from 'react';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  FilterIcon,
  Popover,
  PopoverBody,
  PopoverButtonGroup,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  UserGroupBadge,
  useDisclosure,
} from '@frontend/legacy-components';

import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';

type UserGroupFilterProps = {
  activeGroupIds: number[];
  onApply: (nextActiveGroupIds: number[]) => void;
};

export const UserGroupFilter = ({
  activeGroupIds,
  onApply,
}: UserGroupFilterProps) => {
  const { availableGroups } = useUserGroupCollectionContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [draft, setDraft] = useState(() => new Set(activeGroupIds));

  const toggleGroup = useCallback((id: number) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const allChecked = draft.size === availableGroups.length;

  const handleOpen = useCallback(() => {
    onOpen();
    setDraft(new Set(activeGroupIds));
  }, [onOpen, activeGroupIds]);

  const handleSelectToggle = useCallback(() => {
    if (allChecked) {
      setDraft(new Set());
    } else {
      setDraft(new Set(availableGroups.map((g) => g.id)));
    }
  }, [allChecked, availableGroups]);

  const handleApply = useCallback(() => {
    onApply(Array.from(draft));
    onClose();
  }, [draft, onApply, onClose]);

  const triggerLabel =
    activeGroupIds.length === availableGroups.length ||
    activeGroupIds.length === 0
      ? 'Viewing data for all sites'
      : `Viewing data for ${activeGroupIds.length}/${availableGroups.length} sites`;

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      closeOnBlur={false}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <Button
          variant="link"
          size="md"
          icon={<FilterIcon />}
          onClick={handleOpen}
        >
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="max-content">
          <PopoverHeader>Include/Exclude Site Data</PopoverHeader>
          <PopoverBody>
            <CheckboxGroup direction="column">
              {availableGroups.map((group) => (
                <Checkbox
                  key={group.id}
                  isChecked={draft.has(group.id)}
                  onChange={() => toggleGroup(group.id)}
                >
                  <UserGroupBadge
                    name={group.businessName}
                    icon={group.icon}
                    colorPalette={group.color}
                    size="sm"
                    maxWidth="100%"
                  />
                </Checkbox>
              ))}
            </CheckboxGroup>
          </PopoverBody>
          <PopoverFooter>
            <PopoverButtonGroup>
              <Button variant="secondary" onClick={handleSelectToggle}>
                {allChecked ? 'Deselect All' : 'Select All'}
              </Button>
              <Button onClick={handleApply} isDisabled={draft.size === 0}>
                Apply
              </Button>
            </PopoverButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
